import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, Outlet, useLocation } from 'react-router-dom';
import { Upload, X, FileText, Maximize2, Minimize2 } from 'lucide-react';
import { uploadFileToS3, extractTextFromPDF } from './api';

export default function UploadPdfAssignment() {
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [assignmentData, setAssignmentData] = useState(null);
  const fileInputRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Retrieve assignment data from location state or local storage
    const storedAssignmentData = location.state?.assignmentData || JSON.parse(localStorage.getItem('assignmentData'));
    if (storedAssignmentData) {
      setAssignmentData(storedAssignmentData);
    }
  }, [location.state]);

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file && file.type === 'application/pdf') {
      setSelectedFile(file);
      setPreviewUrl(URL.createObjectURL(file));
      setError(null);
    } else {
      setError('Please select a valid PDF file.');
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      setError('Please select a file to upload.');
      return;
    }
    if (!assignmentData) {
      setError('Assignment data is missing.');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Upload the file to S3
      await uploadFileToS3(selectedFile, assignmentData.title);

      // Extract text from the uploaded PDF and store in MongoDB
      const { assignment } = await extractTextFromPDF(selectedFile, assignmentData.title);

      // Navigate to digitise route with the extracted data
      navigate('digitise', { state: { assignment, assignmentData } });
    } catch (error) {
      console.error('Error processing PDF:', error);
      setError(`An error occurred: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const closePreview = () => {
    setPreviewUrl(null);
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  return (
    <div className="flex flex-col items-start justify-center min-h-screen w-full bg-gradient-to-br from-blue-50 to-indigo-100 p-5">
      <div className="w-full max-w-3xl mx-auto mt-5 bg-white rounded-xl shadow-md p-8">
        <h2 className="text-2xl md:text-3xl font-semibold text-center text-blue-900 mb-6">Upload Assignment</h2>
        {assignmentData && (
          <div className="mb-4 text-sm text-gray-600">
            <span className="font-semibold">Current Assignment:</span> {assignmentData.title}
          </div>
        )}
        <div className="mb-6">
          <input
            type="file"
            accept=".pdf"
            onChange={handleFileChange}
            ref={fileInputRef}
            id="pdf-file-input"
            className="hidden"
          />
          <label
            htmlFor="pdf-file-input"
            className="flex items-center justify-center text-blue-600 p-3.5 rounded-lg cursor-pointer transition duration-300 ease-in-out font-medium hover:bg-blue-100 hover:-translate-y-0.5"
          >
            <Upload size={24} className="mr-2" />
            <span>{selectedFile ? selectedFile.name : 'Choose PDF file'}</span>
          </label>
        </div>
        {error && <p className="text-red-500 mb-4">{error}</p>}
        <button
          className="w-full bg-blue-600 text-white font-semibold py-3.5 px-4 rounded-lg cursor-pointer transition duration-300 ease-in-out mb-6 hover:bg-blue-700 hover:-translate-y-0.5 disabled:bg-gray-400 disabled:cursor-not-allowed"
          onClick={handleUpload}
          disabled={!selectedFile || isLoading}
        >
          {isLoading ? 'Processing...' : 'Upload and Digitise PDF'}
        </button>
        {isLoading && (
          <div className="text-center text-blue-600">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-2"></div>
            <p>Uploading file, extracting and formatting text...</p>
          </div>
        )}
        {previewUrl && (
          <div className={`border border-gray-200 rounded-lg overflow-hidden bg-white ${isFullscreen ? 'fixed inset-0 z-50' : ''}`}>
            <div className="flex justify-between items-center bg-gray-100 p-3.5">
              <div className="flex items-center">
                <FileText size={24} className="text-blue-600 mr-2" />
                <span className="font-semibold text-gray-800">PDF Preview</span>
              </div>
              <div className="flex">
                <button
                  className="p-1 ml-2 text-gray-600 hover:text-blue-600 transition duration-300 ease-in-out"
                  onClick={toggleFullscreen}
                >
                  {isFullscreen ? <Minimize2 size={24} /> : <Maximize2 size={24} />}
                </button>
                <button
                  className="p-1 ml-2 text-gray-600 hover:text-blue-600 transition duration-300 ease-in-out"
                  onClick={closePreview}
                >
                  <X size={24} />
                </button>
              </div>
            </div>
            <div className={`h-[450px] overflow-hidden ${isFullscreen ? 'h-[calc(100vh-70px)]' : ''}`}>
              <iframe src={previewUrl} title="PDF Preview" className="w-full h-full border-none" />
            </div>
          </div>
        )}
      </div>
      <Outlet />
    </div>
  );
}

